import { useState, useCallback } from 'react';
import { dealerPublic } from '../services/dealerApi';

/**
 * Lead-gating hook for public dealer site & car detail.
 *
 * Usage:
 *   const lead = useBuyerLead({ slug, dealer });
 *   <button onClick={() => lead.openFor({ action: 'whatsapp', listing })} />
 *   <BuyerLeadModal {...lead.modalProps} />
 *
 * When the buyer submits the form we POST the lead, then trigger the actual
 * tel: / wa.me redirect.
 */
export const useBuyerLead = ({ slug, dealer }) => {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // pending action: { action, listing, message }
  const [pending, setPending] = useState(null);

  const openFor = useCallback(({ action, listing = null, message = null }) => {
    setPending({ action, listing, message });
    setOpen(true);
  }, []);

  const close = useCallback(() => {
    if (submitting) return;
    setOpen(false);
    setPending(null);
  }, [submitting]);

  const performAction = useCallback(() => {
    if (!pending || !dealer) return;
    if (pending.action === 'whatsapp') {
      const num = (dealer.whatsapp_number || '').replace(/\D/g, '');
      const fallback =
        pending.listing
          ? `Hi, I'm interested in the ${pending.listing.year || ''} ${pending.listing?.ai_details?.make || ''} ${pending.listing?.ai_details?.model || ''} listed on ${dealer.name}.`
          : `Hi, I'm interested in cars from ${dealer.name}.`;
      const text = encodeURIComponent(pending.message || fallback);
      window.open(`https://wa.me/91${num}?text=${text}`, '_blank');
    } else if (pending.action === 'call') {
      const num = dealer.contact_number || dealer.whatsapp_number;
      window.location.href = `tel:${num}`;
    }
  }, [pending, dealer]);

  const onSubmit = useCallback(
    async ({ buyer_name, buyer_mobile }) => {
      if (!pending) return;
      setSubmitting(true);
      try {
        await dealerPublic.submitLead(slug, {
          buyer_name,
          buyer_mobile,
          listing_id: pending.listing?.id || 'site',
          action: pending.action,
        });
        // close, then redirect
        setOpen(false);
        // small timeout allows modal to dismiss before redirect on iOS Safari
        setTimeout(() => performAction(), 100);
      } finally {
        setSubmitting(false);
      }
    },
    [pending, slug, performAction]
  );

  const carLabel = pending?.listing
    ? `${pending.listing.year || ''} ${pending.listing?.ai_details?.make || ''} ${pending.listing?.ai_details?.model || ''}`.trim()
    : '';

  return {
    openFor,
    modalProps: {
      open,
      onClose: close,
      action: pending?.action || 'whatsapp',
      context: { car: carLabel, dealer: dealer?.name },
      submitting,
      onSubmit,
    },
  };
};
